import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  TooltipProps
} from 'recharts';

// デフォルトのカラーパレット。
const DEFAULT_COLORS = [
  "#3cb44b", "#ffe119", "#4363d8", "#46f0f0", "#bcf60c", "#008080", "#9a6324", "#000080", "#aaffc3", "#808000",
  "#ffd8b1", "#000075", "#808080", "#000000", "#a0522d", "#1f77b4", "#2ca02c", "#9467bd", "#8c564b", "#7f7f7f",
  "#bcbd22", "#17becf", "#aec7e8", "#98df8a", "#c5b0d5", "#c49c94", "#c7c7c7",
  "#dbdb8d", "#9edae5", "#393b79", "#637939", "#8c6d31", "#7b4173", "#5254a3",
];

/**
 * 凡例の最大高さ (px)。
 * 凡例の項目数はデータに応じて変動し、折り返し行数がそのままページ全体の高さに反映されるため、
 * 上限を設けてスクロールに逃がし、データ更新時の高さジャンプ（ちらつき）を抑える。
 */
const LEGEND_MAX_HEIGHT = 112;

const DEFAULT_STROKE_WIDTH = 1;
const DEFAULT_SELECTED_STROKE_WIDTH = 4;
const DEFAULT_Z_INDEX = 1;
const DEFAULT_SELECTED_Z_INDEX = 999;
const DEFAULT_AXIS_SIDE_MARGIN = 16;
const AXIS_SIDE_MARGIN_RATIO = 0.25;
const AXIS_TEXT_WIDTH_PADDING = 14;
const MIN_AXIS_WIDTH = 36;
const MAX_AXIS_WIDTH = 120;
const AXIS_FONT = '12px sans-serif';
const TEXT_WIDTH_FALLBACK_PER_CHAR = 8;

export interface ChartSeriesConfig {
  /** 
   * データ配列内のオブジェクトのキー名。
   * このキーを使って、グラフに表示するデータと紐付けを行います。
   */
  dataKey: string;
  /** 凡例やツールチップに表示する名称 */
  name: string;
  /** グラフの種類（折れ線 または 棒グラフ） */
  type: 'line' | 'bar';
  /** 線の色。指定しない場合はデフォルトパレットから自動で割り当てられます */
  color?: string;
  /** 通常時の重なり順（z-index） */
  zIndex?: number;
  /** 通常時の線の太さ */
  strokeWidth?: number;
  /** 
   * 選択（強調）時の線の太さ
   * ※グループ設定がある場合は、グループの設定が優先されます
   */
  selectedStrokeWidth?: number;
  /** 
   * 選択（強調）時の重なり順（z-index）
   * ※グループ設定がある場合は、グループの設定が優先されます
   */
  selectedZIndex?: number;
  /** Y軸のID。左右の軸を使い分ける場合に指定します (例: "left", "right") */
  yAxisId?: string | number;
}

export interface ChartGroupConfig {
  /** グループのタイトル（凡例のセクションタイトルとして表示されます） */
  title: string;
  /** 
   * このグループに含めるデータのキーリスト。
   * ChartSeriesConfigのdataKeyと一致させる必要があります。
   * ここに指定された順序で凡例が表示されます。
   */
  dataKeys: string[];
  /** 
   * このグループに属する項目の選択（強調）時の線の太さ 
   * 個別のSeries設定より優先されます。
   */
  selectedStrokeWidth?: number;
  /** 
   * このグループに属する項目の選択（強調）時の重なり順（z-index） 
   * 個別のSeries設定より優先されます。
   */
  selectedZIndex?: number;
}

export interface ChartCategoryAxisConfig {
  /**
   * カテゴリ軸（通常はX軸）に使用するデータのキー名。
   * どのデータを基準にグラフを描画するかを指定します。
   */
  dataKey: string;
  /** 軸のラベル */
  label?: string;
  /** ラベルの傾き角度（度）。負の値で反時計回り（例: -45） */
  angle?: number;
  /** ラベルと軸線の間のマージン */
  tickMargin?: number;
  /** tick の textAnchor（angle 指定時は "end" を推奨） */
  textAnchor?: "start" | "middle" | "end";
}

export interface ChartValueAxisConfig {
  /** 
   * 数値軸（通常はY軸）のID。
   * 複数の軸を表示する場合や、Seriesと紐付けるために使用します。
   */
  id: string | number;
  /** 軸の表示位置（左右） */
  orientation: 'left' | 'right';
  /** 軸のラベル */
  label?: string;
  /** 目盛り表示用のフォーマッター（表示のみを整形） */
  tickFormatter?: (value: number | string) => string;
  /** 軸領域の幅。目盛りテキストが長い場合に調整できます */
  width?: number;
  /** Y軸の表示範囲。例: [0, 100] で常に0〜100を表示します */
  domain?: [number | string, number | string];
  /** Y軸の目盛り値を明示指定します。CartesianGrid の水平線もこの位置に引かれます */
  ticks?: number[];
}

export interface CustomChartProps {
  /** グラフ全体のタイトル */
  title?: React.ReactNode;
  /** タイトル直下に表示する補足文 */
  description?: string | React.ReactNode;
  /** 
   * グラフに表示するデータの配列
   * 各要素はオブジェクトで、seriesのdataKeyに対応するプロパティを持つ必要があります
   */
  data: any[];
  /** 各データ系列の設定（色、タイプ、名前など） */
  series: ChartSeriesConfig[];
  /** 
   * 凡例のグループ設定。
   * 指定するとグループ化された凡例が表示されます。指定がない場合は通常の凡例が表示されます。
   */
  groups?: ChartGroupConfig[];
  /** 初期状態で選択（強調）されているdataKeyのリスト */
  defaultSelected?: string[];
  /** カスタムツールチップコンポーネント */
  CustomTooltip?: React.FC<TooltipProps<any, any>>;
  /** 
   * カテゴリ軸（X軸）の設定。
   * データのカテゴリ基準となるキー(dataKey)などを指定します。
   */
  categoryAxis: ChartCategoryAxisConfig;
  /** 
   * 数値軸（Y軸）の設定リスト。
   * 左右の軸の配置やラベルなどを指定します。
   * 未指定の場合はデフォルトの左軸が表示されます。
   */
  valueAxes?: ChartValueAxisConfig[];
  /** グラフの高さ */
  height?: number | string;
  /** CartesianGrid の表示設定。未指定時はデフォルト（horizontal/vertical 両方表示） */
  cartesianGrid?: {
    horizontal?: boolean;
    vertical?: boolean;
    stroke?: string;
    strokeDasharray?: string;
  };
  /** Y値を指定して全幅の水平基準線を引く。CartesianGrid より確実に全幅に描画されます */
  referenceLines?: Array<{ y: number; yAxisId?: string | number; stroke?: string; strokeDasharray?: string }>;
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const getAxisKey = (axisId: string | number) => String(axisId);

const getSeriesAxisKey = (series: ChartSeriesConfig) => getAxisKey(series.yAxisId ?? 0);

// 軸目盛りの幅計測はデータ更新のたびに何度も呼ばれるため、
// canvas/context をモジュールスコープで使い回し生成コストを避ける
let measureTextWidthContext: CanvasRenderingContext2D | null | undefined;

const measureTextWidth = (text: string): number => {
  if (typeof document === 'undefined') {
    return text.length * TEXT_WIDTH_FALLBACK_PER_CHAR;
  }

  if (measureTextWidthContext === undefined) {
    const canvas = document.createElement('canvas');
    measureTextWidthContext = canvas.getContext('2d');
    if (measureTextWidthContext) {
      measureTextWidthContext.font = AXIS_FONT;
    }
  }

  if (!measureTextWidthContext) return text.length * TEXT_WIDTH_FALLBACK_PER_CHAR;

  return measureTextWidthContext.measureText(text).width;
};

const getAxisWidthFromValues = (values: number[], formatter?: (value: number | string) => string) => {
  const formatTick = formatter ?? ((value: number | string) => String(value));
  const valuesToMeasure = values.length ? values : [0];

  const maxTextWidth = valuesToMeasure.reduce((maxWidth, value) => {
    return Math.max(maxWidth, measureTextWidth(formatTick(value)));
  }, 0);

  return clamp(Math.ceil(maxTextWidth + AXIS_TEXT_WIDTH_PADDING), MIN_AXIS_WIDTH, MAX_AXIS_WIDTH);
};

const getAxisSideMargin = (axisWidth: number) => {
  if (!axisWidth) return DEFAULT_AXIS_SIDE_MARGIN;
  return Math.max(DEFAULT_AXIS_SIDE_MARGIN, Math.round(axisWidth * AXIS_SIDE_MARGIN_RATIO));
};

export const CustomChart: React.FC<CustomChartProps> = ({
  title,
  description,
  data,
  series,
  groups,
  defaultSelected = [],
  CustomTooltip,
  categoryAxis,
  valueAxes,
  height = 400,
  cartesianGrid,
  referenceLines,
}) => {
  const [selectedKeys, setSelectedKeys] = useState<Record<string, boolean>>(() => {
    const initialSelected: Record<string, boolean> = {};
    defaultSelected.forEach(key => {
      initialSelected[key] = true;
    });
    return initialSelected;
  });

  useEffect(() => {
    const nextSelected: Record<string, boolean> = {};
    defaultSelected.forEach(key => {
      nextSelected[key] = true;
    });
    setSelectedKeys(nextSelected);
  }, [defaultSelected]);

  const handleLegendClick = useCallback((dataKey: string) => {
    setSelectedKeys(prev => ({
      ...prev,
      [dataKey]: !prev[dataKey]
    }));
  }, []);

  const seriesWithColors = useMemo(() => {
    let colorIndex = 0;
    return series.map(s => {
      // 指定された色を使うか、デフォルトパレットから選択
      const color = s.color || DEFAULT_COLORS[colorIndex++ % DEFAULT_COLORS.length];
      return { ...s, assignedColor: color };
    });
  }, [series]);

  const getSeriesConfig = (key: string) => seriesWithColors.find(s => s.dataKey === key);

  const axisWidthsById = useMemo(() => {
    if (!valueAxes?.length) return {} as Record<string, number>;

    return valueAxes.reduce<Record<string, number>>((widthsById, axis) => {
      const axisKey = getAxisKey(axis.id);

      if (axis.width != null) {
        widthsById[axisKey] = axis.width;
        return widthsById;
      }

      const axisValues = seriesWithColors
        .filter((seriesConfig) => getSeriesAxisKey(seriesConfig) === axisKey)
        .flatMap((seriesConfig) => (
          data
            .map((row) => row?.[seriesConfig.dataKey])
            .filter((value) => Number.isFinite(Number(value)))
            .map((value) => Number(value))
        ));

      widthsById[axisKey] = getAxisWidthFromValues(axisValues, axis.tickFormatter);
      return widthsById;
    }, {});
  }, [data, seriesWithColors, valueAxes]);

  const chartMargin = useMemo(() => {
    const leftAxisWidth = valueAxes
      ?.filter((axis) => axis.orientation === 'left')
      .reduce((maxWidth, axis) => Math.max(maxWidth, axisWidthsById[getAxisKey(axis.id)] ?? 0), 0) ?? 0;

    const rightAxisWidth = valueAxes
      ?.filter((axis) => axis.orientation === 'right')
      .reduce((maxWidth, axis) => Math.max(maxWidth, axisWidthsById[getAxisKey(axis.id)] ?? 0), 0) ?? 0;

    return {
      top: 8,
      right: getAxisSideMargin(rightAxisWidth),
      left: getAxisSideMargin(leftAxisWidth),
      // 斜めラベル時は凡例に隠れないよう余白を追加
      bottom: categoryAxis.angle ? 24 : 8,
    };
  }, [axisWidthsById, valueAxes, categoryAxis.angle]);

  const legendListStyle: React.CSSProperties = {
    listStyle: "none",
    padding: 0,
    margin: 0,
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "flex-start",
    alignContent: "flex-start",
    alignItems: "center",
    gap: "15px 8px",
    maxHeight: LEGEND_MAX_HEIGHT,
    overflowY: "auto",
  };

  const renderLegendItem = (s: (typeof seriesWithColors)[number], targetKey?: string) => {
    const key = targetKey ?? s.dataKey;
    const isSelected = !!selectedKeys[key];

    return (
      <li
        key={key}
        style={{
          display: "inline-flex",
          alignItems: "center",
          cursor: "pointer",
          padding: "2px 6px",
          borderRadius: "4px",
          backgroundColor: isSelected ? "rgba(0,0,0,0.05)" : "transparent"
        }}
        onClick={() => handleLegendClick(key)}
      >
        <div
          style={{
            width: 20,
            height: s.type === 'line' ? (isSelected ? 4 : 2) : 10,
            backgroundColor: s.assignedColor,
            marginRight: 8,
            borderRadius: 1,
            transition: s.type === 'line' ? "height 0.2s" : undefined,
          }}
        />
        <span style={{ color: "#666", fontSize: '0.85rem', fontWeight: isSelected ? "bold" : "normal" }}>
          {s.name}
        </span>
      </li>
    );
  };

  const renderLegend = () => {
    if (!groups?.length) {
      return (
        <Paper elevation={0} variant="outlined" sx={{ p: 2, mt: 1 }}>
          <ul style={legendListStyle}>
            {seriesWithColors.map((s) => renderLegendItem(s))}
          </ul>
        </Paper>
      );
    }

    return (
      <Paper elevation={0} variant="outlined" sx={{ p: 2, mt: 1 }}>
        {groups.map(group => (
          <div key={group.title} style={{ display: 'flex', borderBottom: '1px solid #eee', padding: '8px 0' }}>
            <div style={{ width: '180px', flexShrink: 0, paddingRight: '16px', display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {group.title}
              </Typography>
            </div>
            <div style={{ flexGrow: 1 }}>
              <ul style={legendListStyle}>
                {group.dataKeys.map(key => {
                  const s = getSeriesConfig(key);
                  if (!s) return null;
                  return renderLegendItem(s, key);
                })}
              </ul>
            </div>
          </div>
        ))}
      </Paper>
    );
  };

  return (
    <Paper sx={{ display: "flex", flexDirection: "column", border: "1px solid #eee", borderRadius: 4 }}>
      {title}
      {description && (
        <Typography sx={{ px: 2, pb: 1, mt: -1, color: "text.secondary", fontSize: "0.75rem" }}>
          {description}
        </Typography>
      )}
      <Box
        sx={{
          padding: 2,
          '& .recharts-wrapper [tabindex]:focus, & .recharts-wrapper svg:focus, & .recharts-wrapper *:focus-visible': {
            outline: 'none !important',
            boxShadow: 'none !important',
          },
          '& .recharts-wrapper svg': {
            WebkitTapHighlightColor: 'transparent',
          },
        }}
      >

        <div style={{ height, width: "100%" }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={chartMargin} accessibilityLayer={false}>
              <CartesianGrid
                stroke={cartesianGrid?.stroke ?? "#f5f5f5"}
                horizontal={cartesianGrid?.horizontal ?? true}
                vertical={cartesianGrid?.vertical ?? true}
                strokeDasharray={cartesianGrid?.strokeDasharray}
              />
              {referenceLines?.map((rl, i) => (
                <ReferenceLine
                  key={i}
                  y={rl.y}
                  yAxisId={rl.yAxisId}
                  stroke={rl.stroke ?? "#e0e0e0"}
                  strokeDasharray={rl.strokeDasharray}
                />
              ))}
              <XAxis
                dataKey={categoryAxis.dataKey}
                label={categoryAxis.label ? {
                  value: categoryAxis.label,
                  position: 'insideBottomRight',
                  offset: -5
                } : undefined}
                fontSize="0.85rem"
                minTickGap={1}
                angle={categoryAxis.angle}
                tickMargin={categoryAxis.tickMargin}
                tick={categoryAxis.textAnchor !== undefined ? { textAnchor: categoryAxis.textAnchor } : true}
              />
              {valueAxes ? (
                valueAxes.map((axis) => (
                  <YAxis
                    key={axis.id}
                    yAxisId={axis.id}
                    orientation={axis.orientation}
                    width={axis.width ?? axisWidthsById[getAxisKey(axis.id)] ?? 56}
                    tickFormatter={axis.tickFormatter}
                    domain={axis.domain}
                    ticks={axis.ticks}
                    label={axis.label ? {
                      value: axis.label,
                      angle: axis.orientation === 'left' ? -90 : 90,
                      position: axis.orientation === 'left' ? "insideLeft" : "insideRight"
                    } : undefined}
                  />
                ))
              ) : (
                <YAxis />
              )}

              {CustomTooltip ? (
                <Tooltip content={CustomTooltip} offset={30} />
              ) : (
                <Tooltip contentStyle={{ backgroundColor: "rgba(255, 255, 255, 0.6)" }} offset={30} />
              )}

              {seriesWithColors.map(s => {
                const isSelected = selectedKeys[s.dataKey];
                // デフォルトの線の太さ
                const baseStrokeWidth = s.strokeWidth ?? DEFAULT_STROKE_WIDTH;

                // この系列が属するグループ設定を取得
                const groupConfig = groups?.find(g => g.dataKeys.includes(s.dataKey));

                // 選択時のスタイル決定: グループ設定 > 個別設定 > デフォルト値
                const activeStrokeWidth = groupConfig?.selectedStrokeWidth ?? s.selectedStrokeWidth ?? DEFAULT_SELECTED_STROKE_WIDTH;
                // 選択時のz-index決定: グループ設定 > 個別設定 > デフォルト値
                const activeZIndex = groupConfig?.selectedZIndex ?? s.selectedZIndex ?? DEFAULT_SELECTED_Z_INDEX;

                // 選択状態に応じた値の適用
                const strokeWidth = isSelected
                  ? activeStrokeWidth
                  : baseStrokeWidth;

                const baseZIndex = s.zIndex ?? DEFAULT_Z_INDEX;
                const zIndex = isSelected
                  ? activeZIndex
                  : baseZIndex;

                if (s.type === 'bar') {
                  return (
                    <Bar
                      key={s.dataKey}
                      dataKey={s.dataKey}
                      name={s.name}
                      fill={s.assignedColor}
                      barSize={20}
                      yAxisId={s.yAxisId}
                      stroke={s.assignedColor}
                      strokeWidth={isSelected ? strokeWidth : 0}
                    // fillOpacity={isSelected ? 1 : 0.5}
                    />
                  );
                }

                return (
                  <Line
                    key={s.dataKey}
                    type="linear"
                    dataKey={s.dataKey}
                    name={s.name}
                    stroke={s.assignedColor}
                    strokeWidth={strokeWidth}
                    activeDot={{ r: 8 }}
                    dot={false}
                    zIndex={zIndex}
                    yAxisId={s.yAxisId}
                  />
                );
              })}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        {renderLegend()}
      </Box>
    </Paper >
  );
};
